import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Shell, EmptyState, formatMoney } from "./Common";
import { FONT_HEADING } from "../../../theme";

// ── Normalize backend shape ───────────────────────────────────────────────
function normalize(data) {
  if (!data) return null;
  const product = data.product || data;
  const variants = data.variants || product.variants || [];
  const locations = data.locations || data.inventory_levels || [];
  const currency = data.currency || null;
  const unitsSold = data.units_sold ?? data.total_sold ?? null;
  return { product, variants, locations, currency, unitsSold };
}

function parseTags(tags) {
  if (Array.isArray(tags)) return tags.filter(Boolean);
  if (typeof tags === "string")
    return tags.split(",").map((t) => t.trim()).filter(Boolean);
  return [];
}

// ── Status pill (Active / Draft / Archived) ───────────────────────────────
function StatusPill({ status }) {
  const s = (status || "active").toLowerCase();
  const styles = {
    active: { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
    draft: { bg: "#fef3c7", color: "#b45309", border: "#fcd34d" },
    archived: { bg: "#f4f4f5", color: "#52525b", border: "#d4d4d8" },
  };
  const c = styles[s] || styles.archived;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        fontSize: 12,
        fontWeight: 700,
        color: c.color,
        bgcolor: c.bg,
        border: `1px solid ${c.border}`,
        px: "8px",
        py: "1px",
        borderRadius: "9999px",
        textTransform: "capitalize",
        letterSpacing: "0.02em",
        lineHeight: 1.5,
      }}
    >
      {s}
    </Box>
  );
}

// ── Stat tile (icon + label + value + sub) ────────────────────────────────
function StatTile({ icon, label, value, sub, tone = "blue" }) {
  const tones = {
    green: { bg: "#dcfce7", icon: "#15803d", value: "#15803d" },
    blue: { bg: "#dbeafe", icon: "#1d4ed8", value: "#1d4ed8" },
    purple: { bg: "#ede9fe", icon: "#6d28d9", value: "#6d28d9" },
    amber: { bg: "#fef3c7", icon: "#b45309", value: "#b45309" },
  };
  const t = tones[tone] || tones.blue;
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e4e4e7",
        borderRadius: "10px",
        p: "8px 12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          bgcolor: t.bg,
          color: t.icon,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, textAlign: "right" }}>
        <Typography
          sx={{
            fontFamily: FONT_HEADING,
            fontSize: 12,
            fontWeight: 700,
            color: "#71717a",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            lineHeight: 1.3,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_HEADING,
            fontSize: 20,
            fontWeight: 700,
            color: t.value,
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
          }}
        >
          {value}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: 13, color: "#71717a", lineHeight: 1.3 }}>
            {sub}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ── Product header card (image + identity + shop pill) ────────────────────
function ProductCard({ product, storeDomain }) {
  const imgSrc = product.image?.src || product.images?.[0]?.src;
  const title = product.title || "Untitled product";
  const sku = product.variants?.[0]?.sku || "N/A";
  const vendor = product.vendor || "N/A";

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e4e4e7",
        borderRadius: "10px",
        p: "10px 12px",
        display: "flex",
        gap: 1.25,
        alignItems: "center",
      }}
    >
      {/* Thumbnail */}
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "8px",
          bgcolor: "#f4f4f5",
          border: "1px solid #e4e4e7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {imgSrc ? (
          <Box
            component="img"
            src={imgSrc}
            alt={title}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <ShoppingBagOutlinedIcon sx={{ fontSize: 22, color: "#a1a1aa" }} />
        )}
      </Box>

      {/* Identity */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexWrap: "wrap",
            mb: "2px",
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT_HEADING,
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              color: "#09090b",
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          <StatusPill status={product.status || "active"} />
        </Box>
        <Typography sx={{ fontSize: 13, color: "#71717a", lineHeight: 1.35 }}>
          SKU:{" "}
          <Box component="span" sx={{ color: "#3f3f46", fontWeight: 500 }}>
            {sku}
          </Box>{" "}
          ·{" "}
          Vendor:{" "}
          <Box component="span" sx={{ color: "#3f3f46", fontWeight: 500 }}>
            {vendor}
          </Box>
        </Typography>
      </Box>

      {/* Shop pill */}
      {storeDomain && (
        <Box
          sx={{
            fontSize: 12,
            color: "#1e3a8a",
            bgcolor: "#dbeafe",
            border: "1px solid #bfdbfe",
            px: "8px",
            py: "2px",
            borderRadius: "9999px",
            fontFamily: "monospace",
            lineHeight: 1.4,
            flexShrink: 0,
          }}
        >
          {storeDomain}
        </Box>
      )}
    </Box>
  );
}

// ── Variants table card ───────────────────────────────────────────────────
function VariantsCard({ variants, currency }) {
  if (!variants || variants.length === 0) return null;

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e4e4e7",
        borderRadius: "10px",
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: "8px 12px 6px" }}>
        <Typography
          sx={{
            fontFamily: FONT_HEADING,
            fontSize: 13,
            fontWeight: 700,
            color: "#09090b",
            letterSpacing: "-0.01em",
          }}
        >
          Variants
        </Typography>
      </Box>
      <Box
        component="table"
        sx={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}
      >
        <thead>
          <tr>
            {["Variant", "SKU", "Price", "Stock"].map((h) => (
              <Box
                component="th"
                key={h}
                sx={{
                  textAlign: "left",
                  p: "6px 12px",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#52525b",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  borderTop: "1px solid #e4e4e7",
                  borderBottom: "1px solid #e4e4e7",
                  bgcolor: "#fafafa",
                }}
              >
                {h}
              </Box>
            ))}
          </tr>
        </thead>
        <tbody>
          {variants.map((v, i) => {
            const qty = v.inventory_quantity;
            const isOversold = typeof qty === "number" && qty < 0;
            const isLast = i === variants.length - 1;
            const cellBorder = isLast ? "none" : "1px solid #f4f4f5";
            return (
              <tr key={v.id || i}>
                <Box
                  component="td"
                  sx={{
                    p: "7px 12px",
                    borderBottom: cellBorder,
                    color: "#09090b",
                    fontWeight: 500,
                  }}
                >
                  {v.title || "Default"}
                </Box>
                <Box
                  component="td"
                  sx={{
                    p: "7px 12px",
                    borderBottom: cellBorder,
                    fontSize: 13,
                    fontFamily: "monospace",
                    color: "#52525b",
                  }}
                >
                  {v.sku || "—"}
                </Box>
                <Box
                  component="td"
                  sx={{
                    p: "7px 12px",
                    borderBottom: cellBorder,
                    color: "#09090b",
                    fontWeight: 600,
                  }}
                >
                  {formatMoney(v.price, currency)}
                </Box>
                <Box
                  component="td"
                  sx={{
                    p: "7px 12px",
                    borderBottom: cellBorder,
                    color: isOversold ? "#dc2626" : "#09090b",
                    fontWeight: 600,
                  }}
                >
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                    {qty ?? "—"}
                    {isOversold && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          bgcolor: "#fee2e2",
                          color: "#991b1b",
                          px: "6px",
                          py: "0px",
                          borderRadius: "9999px",
                          lineHeight: 1.6,
                        }}
                      >
                        Oversold
                      </Box>
                    )}
                  </Box>
                </Box>
              </tr>
            );
          })}
        </tbody>
      </Box>
    </Box>
  );
}

// ── Tag chips ─────────────────────────────────────────────────────────────
function TagChips({ tags }) {
  if (!tags || tags.length === 0) return null;
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e4e4e7",
        borderRadius: "10px",
        p: "8px 12px",
      }}
    >
      <Typography
        sx={{
          fontFamily: FONT_HEADING,
          fontSize: 11,
          fontWeight: 700,
          color: "#71717a",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          mb: "6px",
        }}
      >
        Tags
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
        {tags.map((tag, i) => (
          <Box
            key={i}
            sx={{
              fontSize: 12,
              bgcolor: "#f4f4f5",
              color: "#09090b",
              border: "1px solid #e4e4e7",
              px: "8px",
              py: "2px",
              borderRadius: "9999px",
              fontWeight: 500,
            }}
          >
            {tag}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Inventory locations ───────────────────────────────────────────────────
function InventoryCard({ locations, variants }) {
  const rows =
    locations && locations.length > 0
      ? locations
      : variants.length > 0
      ? [
          {
            name: "Total",
            address: `${variants.length} variant${variants.length === 1 ? "" : "s"}`,
            available: variants.reduce(
              (s, v) => s + (v.inventory_quantity || 0),
              0
            ),
          },
        ]
      : [];

  if (rows.length === 0) return null;

  const totalUnits = rows.reduce(
    (s, r) => s + (r.available ?? r.inventory_quantity ?? 0),
    0
  );
  const oversoldCount = rows.filter(
    (r) => (r.available ?? r.inventory_quantity ?? 0) < 0
  ).length;
  const isTotalNegative = totalUnits < 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography
        sx={{
          fontFamily: FONT_HEADING,
          fontSize: 13,
          fontWeight: 700,
          color: "#09090b",
          letterSpacing: "-0.01em",
        }}
      >
        Inventory Locations
      </Typography>

      {/* Oversold warning */}
      {oversoldCount > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            p: "8px 12px",
            bgcolor: "#fef3c7",
            border: "1px solid #fcd34d",
            borderRadius: "10px",
          }}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              bgcolor: "#fde68a",
              color: "#b45309",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <WarningAmberOutlinedIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: FONT_HEADING,
                fontSize: 13,
                fontWeight: 700,
                color: "#92400e",
                lineHeight: 1.3,
              }}
            >
              Oversold inventory
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#854d0e", lineHeight: 1.4 }}>
              {oversoldCount} location{oversoldCount === 1 ? "" : "s"} showing
              negative stock — orders have been fulfilled beyond available units.
              Reconcile inventory in Shopify to clear this warning.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Per-location rows */}
      {rows.map((loc, i) => {
        const available = loc.available ?? loc.inventory_quantity ?? 0;
        const isOversold = available < 0;
        const isLow = !isOversold && available <= 20;
        const valueColor = isOversold
          ? "#dc2626"
          : isLow
          ? "#b45309"
          : "#15803d";
        const subLine =
          loc.address ||
          [loc.city, loc.province || loc.region].filter(Boolean).join(", ");
        return (
          <Box
            key={loc.id || i}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: "8px 12px",
              bgcolor: "#fff",
              border: "1px solid #e4e4e7",
              borderRadius: "10px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  bgcolor: isOversold ? "#fee2e2" : "#dbeafe",
                  color: isOversold ? "#991b1b" : "#1d4ed8",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Inventory2OutlinedIcon sx={{ fontSize: 16 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography
                    sx={{
                      fontFamily: FONT_HEADING,
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#09090b",
                      lineHeight: 1.25,
                    }}
                  >
                    {loc.name || loc.location_name || "Location"}
                  </Typography>
                  {isOversold && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        bgcolor: "#fee2e2",
                        color: "#991b1b",
                        px: "6px",
                        py: "0px",
                        borderRadius: "9999px",
                        lineHeight: 1.6,
                      }}
                    >
                      Oversold
                    </Box>
                  )}
                </Box>
                {subLine && (
                  <Typography sx={{ fontSize: 12, color: "#71717a", lineHeight: 1.3 }}>
                    {subLine}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography
                sx={{
                  fontFamily: FONT_HEADING,
                  fontSize: 16,
                  fontWeight: 700,
                  color: valueColor,
                  lineHeight: 1.1,
                }}
              >
                {available}
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#71717a", lineHeight: 1.2 }}>
                {isOversold ? "oversold" : "in stock"}
              </Typography>
            </Box>
          </Box>
        );
      })}

      {/* Total summary */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: "8px 12px",
          bgcolor: "#fff",
          border: "1px solid #e4e4e7",
          borderRadius: "10px",
        }}
      >
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            bgcolor: "#dbeafe",
            color: "#1d4ed8",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Inventory2OutlinedIcon sx={{ fontSize: 16 }} />
        </Box>
        <Typography sx={{ fontSize: 13, color: "#52525b", lineHeight: 1.3 }}>
          Total inventory:{" "}
          <Box
            component="strong"
            sx={{
              color: isTotalNegative ? "#dc2626" : "#09090b",
              fontWeight: 700,
            }}
          >
            {totalUnits} unit{Math.abs(totalUnits) === 1 ? "" : "s"}
          </Box>{" "}
          across {rows.length} location{rows.length === 1 ? "" : "s"}
        </Typography>
      </Box>
    </Box>
  );
}

// ── Entity component ──────────────────────────────────────────────────────
export default function Product({ data, storeDomain, loading }) {
  const normalized = normalize(data);

  if (!loading && !normalized?.product) {
    return (
      <Shell bare loading={false}>
        <EmptyState
          icon="🛍️"
          title="No product data"
          message="No product details available."
        />
      </Shell>
    );
  }

  const { product, variants, locations, currency, unitsSold } = normalized || {};
  const tags = parseTags(product?.tags);

  const price = variants?.[0]?.price ?? "0.00";
  const compareAt = variants?.[0]?.compare_at_price ?? "0.00";
  const sold =
    unitsSold ?? (variants || []).reduce((s, v) => s + (v.units_sold || 0), 0);

  return (
    <Shell bare loading={loading}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <ProductCard product={product} storeDomain={storeDomain} />

        <Box
          sx={{
            display: "grid",
            gap: 1,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(3, 1fr)",
            },
          }}
        >
          <StatTile
            icon={<LocalOfferOutlinedIcon sx={{ fontSize: 18 }} />}
            label="Compare At"
            value={formatMoney(compareAt, currency)}
            tone="blue"
          />
          <StatTile
            icon={<ShoppingBagOutlinedIcon sx={{ fontSize: 18 }} />}
            label="Units Sold"
            value={sold || 0}
            tone="purple"
          />
          <StatTile
            icon={<AttachMoneyOutlinedIcon sx={{ fontSize: 18 }} />}
            label="Price"
            value={formatMoney(price, currency)}
            tone="green"
          />
        </Box>

        <VariantsCard variants={variants} currency={currency} />

        <TagChips tags={tags} />

        <InventoryCard locations={locations} variants={variants} />
      </Box>
    </Shell>
  );
}
